import React, { useState, useRef, useEffect } from 'react';
import SaveButtonWithModal from "./SaveButtonWithModel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

const person = [
  "Male", "Female", "Youth ","Physical ",
];

// Validation helpers
const isValidName = (name) => {
  if (!name) return false;
  const words = name.trim().split(/\s+/);
  return words.length <= 8 && /^[A-Za-z\s]+$/.test(name);
};
const isValidSurname = (surname) => {
  if (!surname) return false;
  const words = surname.trim().split(/\s+/);
  return words.length <= 8 && /^[A-Za-z\s]+$/.test(surname);
};
// Accept Date object or string in YYYY-MM-DD
const isValidDateOfBirth = (dob) => {
  if (!dob) return false;
  if (dob instanceof Date && !isNaN(dob)) return true;
  return /^\d{4}-\d{2}-\d{2}$/.test(dob);
};
const isValidId = (id) => {
  return id && /^[A-Za-z0-9]+$/.test(id) && id.length >= 5;
};
const isValidMobile = (mobile) => {
  return /^\+?[0-9\s\-]{7,20}$/.test(mobile);
};
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};
const isValidPassword = (password) => {
  return /^(?=.*[A-Za-z])(?=.*\d).{6,}$/.test(password);
};
const isValidWhatsapp = (whatsapp) => {
  return /^\+?[0-9\s\-]{7,20}$/.test(whatsapp);
};
const isValidPhysicalAddress = (address) => {
  return address && address.length >= 5 && address.length <= 100;
};
const isValidPostalCode = (code) => {
  return /^\d{4,6}$/.test(code);
};

const handleSelect = (option, formData, onFormDataChange) => {
  onFormDataChange({ ...formData, typeOfId: option });
};

const requiredFields = [
  "firstName", "surname", "dateOfBirth", "typeOfId", "idNumber",
  "mobile", "email", "password", "whatsapp", "physicalAddress", "postalCode"
];
const fieldValidators = {
  firstName: isValidName,
  surname: isValidSurname,
  dateOfBirth: isValidDateOfBirth,
  typeOfId: (val) => !!val && val.trim() !== "",
  idNumber: isValidId,
  mobile: isValidMobile,
  email: isValidEmail,
  password: isValidPassword,
  whatsapp: isValidWhatsapp,
  physicalAddress: isValidPhysicalAddress,
  postalCode: isValidPostalCode,
};

const fieldErrorMessages = {
  firstName: "First Name: max 8 words, only letters.",
  surname: "Surname: max 8 words, only letters.",
  dateOfBirth: "Date required (YYYY-MM-DD).",
  typeOfId: "Type of ID is required.",
  idNumber: "Alphanumeric, at least 5 characters.",
  mobile: "Enter a valid phone number (may start with +).",
  whatsapp: "Enter a valid WhatsApp number (may start with +).",
  email: "Invalid email address.",
  password: "Min 6 chars, 1 letter & 1 number.",
  physicalAddress: "5-100 characters.",
  postalCode: "4-6 digits.",
};
const CustomDateDiv = React.forwardRef(({ value, onClick }, ref) => (
  <div
    onClick={onClick}
    ref={ref}
    className="xl:w-[390px] lg:w-[330px] md:w-[250px] w-[115px] h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] text-[#979797] border rounded-l-[5px] bg-[#E7EBEB] border-[#E7EBEB] cursor-pointer font-bold"
  >
    {value || "YYYY-MM-DD"}
  </div>
));
const PersonalInfoForm = ({
  formData,
  onFormDataChange,
  race,
  onRaceChange,
  gender: selectedGender,
  onGenderChange,
  disability,
  onDisabilityChange,
  onSave
}) => {
  const [showModal, setShowModal] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Validate all fields on change and on submit
  const validateAllFields = (data = formData) => {
    let errors = {};
    requiredFields.forEach((field) => {
      const value = data[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        errors[field] = "This field is required.";
      } else if (fieldValidators[field] && !fieldValidators[field](value)) {
        errors[field] = fieldErrorMessages[field];
      }
    });
    return errors;
  };

  // Real-time validation per field
  const handleChange = (e) => {
    const { name, value } = e.target;
    onFormDataChange({ ...formData, [name]: value });

    let error = "";
    if (!value || value.trim() === "") {
      error = "This field is required.";
    } else if (fieldValidators[name] && !fieldValidators[name](value)) {
      error = fieldErrorMessages[name];
    }
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };
  const options = ["Identity card", "Passport", "Drivers License"];

  const UnselectedIcon = () => (
    <svg
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
    >
      <circle cx="8.5" cy="8.5" r="8" fill="#D9D9D9" stroke="none" />
    </svg>
  );
  
  const SelectedIcon = () => (
    <svg
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
    >
      <circle cx="8.5" cy="8.5" r="8" fill="#25242C" stroke="#FFBD26" />
      <path
        d="M5 8.5L7.5 11L12 6"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
  
  // Date picker change handler
  const handleDateChange = (date) => {
    onFormDataChange({ ...formData, dateOfBirth: date });
    let error = "";
    if (!date) {
      error = "This field is required.";
    } else if (!isValidDateOfBirth(date)) {
      error = fieldErrorMessages.dateOfBirth;
    }
    setFieldErrors((prev) => ({ ...prev, dateOfBirth: error }));
  };
  const handleSelect = (option) => {
    onFormDataChange((prev) => ({ ...prev, typeOfId: option }));
    setIsOpen(false); // Close dropdown after selection
  };

  // Check for empty gender/disability
  const handleGenderChange = (val) => {
    onGenderChange(val);
    if (!val) {
      setFieldErrors((prev) => ({ ...prev, gender: "Please select gender." }));
    } else {
      setFieldErrors((prev) => {
        const { gender, ...rest } = prev;
        return rest;
      });
    }
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([
    "Physical Disability",
    "Visual Impairment",
    "Hearing Impairment",
    "Intellectual Disability",
    "Mental Health Condition"
  ]);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleDisabilityChange = (val) => {
    onDisabilityChange(val);
    if (!val) {
      setFieldErrors((prev) => ({ ...prev, disability: "Please select disability/ability." }));
    } else {
      setFieldErrors((prev) => {
        const { disability, ...rest } = prev;
        return rest;
      });
    }
  };
  const dropdownVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      y: -10,
      transition: {
        duration: 0.15
      }
    }
  };

  const chevronVariants = {
    open: { rotate: 180 },
    closed: { rotate: 0 }
  };
  // On Save: validate all
  const handleSave = () => {
    const errors = validateAllFields();
    if (!selectedGender) errors.gender = "Please select gender.";
    if (!disability) errors.disability = "Please select disability/ability.";
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSubmitError("Please fix the errors below.");
      return;
    }
    setSubmitError("");
    setShowModal(true);
    onSave({
      ...formData,
      race,
      gender: selectedGender,
      disability,
    });
  };
  const [isSelected, setSelected] = useState();

  return (
    <div className="mx-auto md:mt-3 mt-1">
     <div className="flex-grow ">
          <h2 className="font-bold text-[10px] text-center  underline md:text-[36px] text-black">
          Section 1 : Individual information
          </h2>
          <p className="text-[8px] md:text-[24px] italic text-black md:mt-2 md:mb-4 mb-2">
          Please enter your information in the section below.
          </p>
        </div>
      {submitError && <div className="text-red-600 text-center mb-1 text-sm">{submitError}</div>}
      {/* FORM */}
      <form onSubmit={e => e.preventDefault()}>
        <div>
          <div className="flex flex-col md:gap-y-3 gap-1 w-full">
            {/* Row 1 */}
            <div className="flex md:flex-row gap-1">
              {/* First Name */}
              <div className="w-full">
              <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Name</label>
                <input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="XXXX"
                  required
                 className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.firstName && <div className="text-red-500 text-xs mt-1">{fieldErrors.firstName}</div>}
              </div>
              {/* Surname */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Surname</label>
                <input
                  name="surname"
                  value={formData.surname}
                  onChange={handleChange}
                  placeholder="XXXX"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px]   font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.surname && <div className="text-red-500 text-xs mt-1">{fieldErrors.surname}</div>}
              </div>
            </div>
            {/* Row 2 */}
            <div className="flex md:flex-row md:gap-x-2  gap-x-1">
              {/* Type of ID */}
              <div className="w-full relative">
      <div className="flex justify-between items-center cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
        <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black">
          <span className="text-red-500">*</span>Identification document
        </label>
      </div>

      <motion.div
        className="w-full h-[30px] md:h-[71px] py-1 md:py-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-medium border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] bg-[#F5F5F5] flex items-center justify-between cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ backgroundColor: "#EDEDED" }}
        whileTap={{ scale: 0.98 }}
      >
        <span className="truncate">{formData.typeOfId || "National Identity Card"}</span>
        <div className="bg-[#E7EBEB] md:w-[105px] w-[30px] rounded-r-[10px] flex justify-center items-center p-1 h-[30px] md:h-[71px]">
          <motion.img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750801032/Expand_down_1_joem7t.png"
            alt="Dropdown icon"
            className="md:w-[50px] md:h-[50px] w-[10px] h-[10px]"
            variants={chevronVariants}
            animate={isOpen ? "open" : "closed"}
            transition={{ duration: 0.2 }}
          />
        </div>
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute z-10 w-full bg-white border-[#979797] border-b-[3px] rounded-md text-black mt-1 max-h-60 overflow-auto shadow-lg"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={dropdownVariants}
          >
            {options.map((option, index) => (
              <motion.div
                key={index}
                onClick={() => handleSelect(option)}
                className="px-2 py-1 text-[8px] md:text-[24px] hover:bg-[#F5F5F5] cursor-pointer flex items-center gap-2"
                whileHover={{ backgroundColor: "#F5F5F5" }}
                whileTap={{ scale: 0.98 }}
              >
                {formData.typeOfId === option ? <SelectedIcon /> : <UnselectedIcon />}
                {option}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {fieldErrors?.typeOfId && (
        <motion.div 
          className="text-red-500 text-xs mt-1"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {fieldErrors.typeOfId}
        </motion.div>
      )}
    </div>
  

              {/* ID Number */}
              <div className="w-full relative">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Identification number on document</label>
                <input
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  required
                  placeholder="XXXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px]  font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[8px] placeholder:font-bold md:placeholder:text-[24px] cursor-pointer"
                />
                {fieldErrors.idNumber && <div className="text-red-500 text-xs mt-1">{fieldErrors.idNumber}</div>}
              </div>
            </div>
            {/* Row 3 */}
            <div className="flex md:flex-row md:gap-x-2  gap-x-1">
              {/* WhatsApp */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span> Mobile  number</label>
                <input
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  required
                  placeholder="XXXXXXXXXX"
              className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px]   font-bold border-b-[3px]  border-[#979797] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.whatsapp && <div className="text-red-500 text-xs mt-1">{fieldErrors.whatsapp}</div>}
              </div>
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span> WhatsApp number</label>
                <input
                  name="mobile"
                  type="text"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="0XXXXXXXXXXXX"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-0 border-b-[3px] border-[#979797] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"

                />
                {fieldErrors.mobile && <div className="text-red-500 text-xs mt-1">{fieldErrors.mobile}</div>}
              </div>
            </div>
            <div className="flex md:flex-row md:gap-x-2  gap-x-1">
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span> Email</label>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="XXXXXX"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px]  font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.email && <div className="text-red-500 text-xs mt-1">{fieldErrors.email}</div>}
              </div>
              <div className="w-full">
          <div className="flex justify-between items-center">
          <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span> Password</label>
          <p className='md:text-[20px] text-[4px] font-normal italic'> Set a password to secure your website profile</p>
          </div>
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="XXXXXXX"
                    className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-0 border-b-[3px] border-[#979797] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"

                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-2 md:text-[30px] text-[8px] top-1/2 transform -translate-y-1/2 text-[#979797]"
                  >
                    {showPassword ? <FiEye /> : <FiEyeOff />}
                  </button>
                </div>
                {fieldErrors.password && <div className="text-red-500 text-xs mt-1">{fieldErrors.password}</div>}
              </div>
            </div>
            {/* Row 4 */}
            <div className="flex md:flex-row md:gap-x-2  gap-x-1">
              {/* Physical Address */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span> Physical Address</label>
                <input
                  name="physicalAddress"
                  value={formData.physicalAddress}
                  onChange={handleChange}
                  required
                  placeholder="XXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px]  font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.physicalAddress && <div className="text-red-500 text-xs mt-1">{fieldErrors.physicalAddress}</div>}
              </div>
              {/* Postal Code */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span> Postal Code</label>
                <input
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  required
                  placeholder="XXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px]  font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.postalCode && <div className="text-red-500 text-xs mt-1">{fieldErrors.postalCode}</div>}
              </div>
            </div>
          </div>
        </div>
      </form>
      {/* Disability Section */}
      
      {/* Save button */}
      
      {/* Save Modal */}
      {showModal && (
        <SaveButtonWithModal
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default PersonalInfoForm;